import { useCallback, useEffect, useRef, useState } from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard, ShoppingBag, Package, BarChart2,
  Settings, Shield, Menu, X, User, LifeBuoy,
} from 'lucide-react';
import LanguageSwitcher from './LanguageSwitcher';
import BrandWordmark from './BrandWordmark';
import UserMenu from './UserMenu';
import MerchantDemoBanner from './MerchantDemoBanner';
import DemoPersistToast from './DemoPersistToast';
import { useFocusTrap } from '../hooks/useFocusTrap';

interface LayoutProps {
  adminMode?: boolean;
}

interface NavItem {
  to: string;
  icon: React.ReactNode;
  label: string;
}

/** Verrouille le scroll du body quand le drawer est ouvert */
function useBodyScrollLock(locked: boolean): void {
  useEffect(() => {
    if (!locked) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [locked]);
}

export default function Layout({ adminMode }: LayoutProps) {
  const { t } = useTranslation();
  const location = useLocation();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const drawerRef = useRef<HTMLDivElement | null>(null);

  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  // Fermer le drawer à chaque changement de route
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // Focus trap + fermeture ESC (le hook existant gère les deux)
  useFocusTrap(drawerRef, drawerOpen, closeDrawer);

  // Bloquer le scroll du body quand le drawer est ouvert
  useBodyScrollLock(drawerOpen);

  const merchantNav: NavItem[] = [
    { to: '/dashboard', icon: <LayoutDashboard size={20} />, label: t('nav.dashboard') },
    { to: '/orders',    icon: <ShoppingBag size={20} />,     label: t('nav.orders') },
    { to: '/products',  icon: <Package size={20} />,          label: t('nav.products') },
    { to: '/stats',     icon: <BarChart2 size={20} />,        label: t('nav.stats') },
    { to: '/settings',  icon: <Settings size={20} />,         label: t('nav.settings') },
    { to: '/assistance', icon: <LifeBuoy size={20} />, label: t('nav.support') },
  ];

  const adminNav: NavItem[] = [
    { to: '/admin', icon: <Shield size={20} />, label: t('admin.title') },
    { to: '/admin/profile', icon: <User size={20} />, label: t('admin.myProfile') },
    { to: '/support', icon: <LifeBuoy size={20} />, label: t('nav.support') },
  ];

  const navItems = adminMode ? adminNav : merchantNav;
  const subtitle  = adminMode ? t('admin.console') : t('nav.dashboard');

  const sidebarAccent = adminMode ? 'bg-violet-600' : 'bg-primary-700';
  const activeNavClass = adminMode
    ? 'bg-violet-50 text-violet-700 shadow-sm'
    : 'bg-primary-50 text-primary-700 shadow-sm';

  /** Contenu du panneau de navigation (partagé sidebar + drawer) */
  const navPanel = (
    <>
      {/* Logo */}
      <div className="p-6 border-b border-gray-100">
        <div className="flex items-center gap-2.5">
          <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${adminMode ? sidebarAccent : ''}`}>
            {adminMode
              ? <Shield size={18} className="text-white" />
              : <img src="/logo-mark.svg" alt="" width={36} height={36} className="w-9 h-9 rounded-xl" aria-hidden />
            }
          </div>
          <div>
            <p className="text-base">
              <BrandWordmark admin={adminMode} />
            </p>
            <p className="text-xs text-gray-400">{subtitle}</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto" aria-label={adminMode ? t('admin.title') : t('nav.dashboard')}>
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/admin'}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors min-h-[44px] ${
                isActive ? activeNavClass : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              }`
            }
          >
            {item.icon}
            {item.label}
          </NavLink>
        ))}
      </nav>

      {/* Langue + User */}
      <div className="p-4 border-t border-gray-100 space-y-3 overflow-visible relative z-20">
        <LanguageSwitcher variant="pill" />
        <UserMenu
          profilePath={adminMode ? '/admin/profile' : '/settings'}
          placement={adminMode ? 'bottom' : 'top'}
          align="left"
          showInlineLogout
          accentClass={adminMode ? 'bg-violet-600' : 'bg-primary-700'}
        />
      </div>
    </>
  );

  const hamburgerLabel = drawerOpen ? t('nav.closeMenu') : t('nav.openMenu');

  return (
    <div className="min-h-[100dvh] bg-cream-100 flex">

      {/* ── SIDEBAR DESKTOP (≥ 768px) ─────────────────────────────── */}
      <aside
        className="hidden md:flex w-64 bg-white border-r border-gray-100 flex-col fixed h-full z-10 shadow-sm"
        aria-label={t('nav.mainNav')}
      >
        {navPanel}
      </aside>

      {/* ── BARRE MOBILE + BOUTON HAMBURGER (< 768px) ─────────────── */}
      <header className="md:hidden fixed top-0 left-0 right-0 z-30 bg-white border-b border-gray-100 shadow-sm pt-[env(safe-area-inset-top)]">
        <div className="flex items-center gap-3 px-4 h-14 min-w-0">
          <button
            type="button"
            onClick={() => setDrawerOpen((v) => !v)}
            aria-expanded={drawerOpen}
            aria-controls="mobile-drawer"
            aria-label={hamburgerLabel}
            className="min-w-[44px] min-h-[44px] flex items-center justify-center rounded-xl text-gray-600 hover:bg-gray-100 transition-colors"
          >
            {drawerOpen ? <X size={22} /> : <Menu size={22} />}
          </button>

          <div className="flex items-center gap-2 min-w-0 flex-1">
            <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${adminMode ? sidebarAccent : ''}`}>
              {adminMode
                ? <Shield size={14} className="text-white" />
                : <img src="/logo-mark.svg" alt="" width={28} height={28} className="w-7 h-7 rounded-lg" aria-hidden />
              }
            </div>
            <BrandWordmark admin={adminMode} className="text-sm truncate" />
            <span className="text-xs text-gray-400 truncate hidden sm:inline">{subtitle}</span>
          </div>
        </div>
      </header>

      {/* ── DRAWER MOBILE (< 768px, overlay) ──────────────────────── */}
      <div
        id="mobile-drawer"
        role="dialog"
        aria-modal="true"
        aria-label={t('nav.mainNav')}
        aria-hidden={!drawerOpen}
        className={`md:hidden fixed inset-0 z-40 transition-opacity duration-200 ${
          drawerOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        {/* Overlay */}
        <button
          type="button"
          aria-label={t('nav.closeMenu')}
          onClick={closeDrawer}
          className="absolute inset-0 bg-black/40 cursor-default w-full h-full border-0"
          tabIndex={drawerOpen ? 0 : -1}
        />

        {/* Panneau */}
        <div
          ref={drawerRef}
          className={`absolute left-0 top-0 h-full w-72 max-w-[85vw] bg-white flex flex-col shadow-2xl transition-transform duration-200 ${
            drawerOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          {/* Bouton fermeture dans le panneau */}
          <div className="flex justify-end px-4 pt-4 pb-2">
            <button
              type="button"
              onClick={closeDrawer}
              aria-label={t('nav.closeMenu')}
              className="min-w-[44px] min-h-[44px] flex items-center justify-center rounded-xl text-gray-500 hover:bg-gray-100 transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          {/* Même contenu que la sidebar */}
          {navPanel}
        </div>
      </div>

      {/* ── CONTENU PRINCIPAL ──────────────────────────────────────── */}
      {/* Desktop : décalé par la sidebar fixe */}
      {/* Mobile  : décalé par la topbar fixe de 56px (h-14) */}
      <main className="flex-1 md:ml-64 pt-[calc(3.5rem+env(safe-area-inset-top))] md:pt-0 min-h-[100dvh] flex flex-col min-w-0">
        {!adminMode && <MerchantDemoBanner />}
        <DemoPersistToast />
        <div className="flex-1">
          <Outlet />
        </div>
      </main>

    </div>
  );
}
